import { proxy, type Snapshot } from 'valtio/vanilla'
import type { TicketListItem } from '@zendesk/zqa-services/tickets'
import { formatCount } from '@klausapp/shared/utils/number'
import { draftContextMatches } from '../draft-context'
import { getSharedState } from '../valtio'

// The data/navigation slice of the old useConversationList composable. Holds the
// loaded ticket list + count + loading/search state; the actual fetch (getTickets)
// stays in the Vue component for now. Selection/navigation logic lives here as
// pure selectors so every consumer derives next/previous from one source.

// Moved verbatim from app `modules/conversations/types.ts` (its one home is now
// here; the app file re-exports it so existing imports are unchanged). Move, not
// refactor — the next/previous selectors are keyed by this type, so it belongs
// with them rather than being widened at the bridge.
export interface TicketListItemExt extends TicketListItem {
  messageId?: string
  subject: string
  id: any
  reviewId?: string | undefined
}

export type TicketListItemSnapshot = Snapshot<TicketListItemExt>

// The three route params the selectors compare against (mirrored from vue-router
// via ticketRouteParamsState).
export interface RouteContext {
  connectionId?: number | string | null
  conversationId?: string
  messageId?: string | null
}

export interface ConversationListState {
  ticketList: TicketListItemExt[]
  ticketListCount: number | undefined
  ticketListLoading: boolean
  ticketListError: boolean
  searchQuery: string
}

// Narrower shape for read-only selectors below: a readonly array accepts both
// the live mutable state and a frozen valtio snapshot() with no cast needed,
// since a mutable array satisfies a readonly-typed parameter but not vice versa.
type ReadableConversationListState = Omit<ConversationListState, 'ticketList'> & {
  ticketList: readonly TicketListItemSnapshot[]
}

export const conversationListState = getSharedState('conversation-list', () =>
  proxy<ConversationListState>({
    ticketList: [],
    ticketListCount: undefined,
    ticketListLoading: true,
    ticketListError: false,
    searchQuery: '',
  }),
)

// --- Pure helpers (moved from useConversationList, names preserved) ---

const ticketContext = (ticket: TicketListItemSnapshot): RouteContext => ({
  connectionId: ticket.paymentTokenId,
  conversationId: ticket.externalId,
  messageId: ticket.messageId,
})

const ticketMatchesCurrentConversation = (ticket: TicketListItemSnapshot, route: RouteContext) =>
  String(ticket.paymentTokenId ?? '') === String(route.connectionId ?? '') && ticket.externalId === route.conversationId

// --- Pure selectors (state + route passed in instead of read from closure refs) ---

export const currentTicketIndex = (state: ReadableConversationListState, route: RouteContext): number => {
  if (!state.ticketList.length) return -1

  if (route.messageId) {
    const messageTicketIndex = state.ticketList.findIndex(
      (ticket) => !!ticket.messageId && draftContextMatches(ticketContext(ticket), route),
    )

    if (messageTicketIndex !== -1) return messageTicketIndex
  }

  return state.ticketList.findIndex((ticket) => ticketMatchesCurrentConversation(ticket, route))
}

export const nextTicket = (state: ReadableConversationListState, route: RouteContext) => {
  if (!state.ticketList.length) return
  if (currentTicketIndex(state, route) === -1) return

  return state.ticketList[currentTicketIndex(state, route) + 1]
}

export const previousTicket = (state: ReadableConversationListState, route: RouteContext) => {
  if (!state.ticketList.length) return
  if (currentTicketIndex(state, route) === -1) return

  const index = currentTicketIndex(state, route) - 1
  return index === -1 ? undefined : state.ticketList[index]
}

export const formattedListCount = (state: ReadableConversationListState, features: Record<string, boolean>) => {
  const len = state.ticketListCount

  if (!features.showExactTicketCount) {
    // (len ?? 0) only to satisfy the state package's stricter null checks — behaviour is
    // identical to the original `len >= 999` (undefined >= 999 is also false).
    return (len ?? 0) >= 999 ? '999+' : len
  }

  return len ? formatCount(len) : ''
}
